import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImagePlus, Upload, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Unified image picker that supports both file upload and URL ingestion.
 * Both methods normalize to a storage path consumed via `${API}/files/{path}`.
 *
 * Props:
 *   path      - current image storage path (or null)
 *   onChange  - (path|null) => void
 *   size      - "sm" | "md" | "lg" (preview thumbnail size)
 *   testid    - data-testid prefix for the inputs
 *   label     - optional label above
 */
export default function ImagePicker({ path, onChange, size = "md", testid = "image", label }) {
  const fileRef = useRef(null);
  const [mode, setMode] = useState("file"); // "file" | "url"
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [errored, setErrored] = useState(false);

  const dim = size === "sm" ? "w-16 h-16" : size === "lg" ? "w-28 h-20" : "w-20 h-20";

  // reset error when path changes
  React.useEffect(() => { setErrored(false); }, [path]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB");
      return;
    }
    if (!/^image\//.test(f.type) && !/\.(jpg|jpeg|png|gif|webp)$/i.test(f.name)) {
      toast.error("Pick a JPG, PNG, GIF or WebP file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.path);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — try a different image");
    }
    setBusy(false);
  };

  const onUrl = async () => {
    const u = url.trim();
    if (!u) {
      toast.error("Paste an image URL");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/upload-image-url", { url: u });
      onChange(data.path);
      setUrl("");
      toast.success("Image fetched");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch image");
    }
    setBusy(false);
  };

  return (
    <div>
      {label && <Label className="label-overline">{label}</Label>}
      <div className={`flex items-start gap-3 ${label ? "mt-2" : ""}`}>
        <div className={`${dim} border border-[#e5e5e5] bg-[#fafafa] overflow-hidden flex items-center justify-center shrink-0 relative`}>
          {path && !errored ? (
            <img
              src={`${API}/files/${path}`}
              alt=""
              className="w-full h-full object-cover"
              onError={() => setErrored(true)}
              onLoad={() => setErrored(false)}
            />
          ) : (
            <div className="text-center px-2">
              <ImagePlus className={`w-4 h-4 mx-auto ${errored ? "text-[#e63946]" : "text-[#a1a1aa]"}`} />
              {errored && (
                <span className="block mt-1 text-[9px] font-mono uppercase tracking-wider text-[#e63946]">
                  broken
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Mode switcher */}
          <div className="inline-flex border border-[#e5e5e5] mb-2">
            <button
              type="button"
              onClick={() => setMode("file")}
              data-testid={`${testid}-mode-file`}
              className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors ${
                mode === "file" ? "bg-[#111] text-white" : "bg-white text-[#52525b] hover:bg-[#fafafa]"
              }`}
            >
              <Upload className="w-3 h-3" /> File
            </button>
            <button
              type="button"
              onClick={() => setMode("url")}
              data-testid={`${testid}-mode-url`}
              className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors border-l border-[#e5e5e5] ${
                mode === "url" ? "bg-[#111] text-white" : "bg-white text-[#52525b] hover:bg-[#fafafa]"
              }`}
            >
              <LinkIcon className="w-3 h-3" /> URL
            </button>
          </div>

          {mode === "file" ? (
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid={`${testid}-file-input`} />
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                className="rounded-none border-[#e5e5e5] h-9"
                data-testid={`${testid}-file-button`}
              >
                {busy ? "Uploading…" : path ? "Replace file" : "Choose file"}
              </Button>
              {path && (
                <button
                  type="button"
                  onClick={() => onChange(null)}
                  className="text-xs text-[#e63946]"
                  data-testid={`${testid}-remove`}
                >
                  Remove
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onUrl(); } }}
                placeholder="https://images.unsplash.com/…"
                className="rounded-none border-[#e5e5e5] h-9 font-mono text-xs flex-1 min-w-0"
                data-testid={`${testid}-url-input`}
              />
              <Button
                type="button"
                onClick={onUrl}
                disabled={busy || !url.trim()}
                className="rounded-none bg-[#111] hover:bg-black text-white h-9"
                data-testid={`${testid}-url-button`}
              >
                {busy ? "Fetching…" : "Fetch"}
              </Button>
              {path && (
                <button
                  type="button"
                  onClick={() => onChange(null)}
                  className="text-xs text-[#e63946]"
                  data-testid={`${testid}-remove`}
                >
                  Remove
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
